import { use_effect } from '@/hooks/_base/use_effect';
import { cn } from '@/utils/utils_client';
import { forwardRef, useImperativeHandle, useRef } from 'react';

interface Props extends React.ComponentProps<'textarea'> {
  lineCount?: number;
}

export const UI_PRIMITIVE__text_area = forwardRef<HTMLTextAreaElement, Props>(({ lineCount = 5, className, ...props }, ref) => {
  const ref_textarea = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(ref, () => ref_textarea.current!, []);

  /*
   * EFFECTS
   */

  use_effect(() => {
    const textarea_element = ref_textarea.current;
    if (!textarea_element) return;

    on_input();

    textarea_element.addEventListener('input', on_input);

    return () => textarea_element.removeEventListener('input', on_input);
  }, []);

  use_effect(() => on_input(), [props.value]);

  /*
   * EVENTS
   */

  function on_input() {
    if (!ref_textarea.current) return;

    const textarea = ref_textarea.current;
    textarea.style.height = 'auto';
    const line_height = 24;
    const height_max = line_height * lineCount;
    const height_new = Math.min(textarea.scrollHeight, height_max);
    textarea.style.height = `${height_new}px`;
  }

  /*
   * METHODS
   */

  /*
   * HTML
   */

  return (
    <textarea
      className={cn(
        `flex py-2 overflow-y-auto
        min-h-10 w-full 
        rounded-md 
        border border-input bg-background  
        text-base ring-offset-background 
        file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground 
        placeholder:text-component-item 
        focus-primary 
        disabled:cursor-not-allowed disabled:opacity-50 
        md:text-sm 
        resize-none  cursor-text`,
        className,
      )}
      ref={ref_textarea}
      {...props}
    />
  );
});

UI_PRIMITIVE__text_area.displayName = 'Textarea';
